import React from 'react';

/**
 * Skeleton placeholder for one column of the main 3-column layout. Mirrors the
 * real column's card chrome so the swap to real content causes minimal layout
 * shift. Decorative — hidden from assistive tech (the shell announces status
 * via the live region in AppLoadingShell).
 */
const SkeletonColumn: React.FC<{ rows?: number }> = ({ rows = 5 }) => (
  <div
    className="bg-white rounded-xl border border-gray-200 p-4 overflow-hidden h-full"
    aria-hidden="true"
  >
    <div className="animate-pulse space-y-4">
      <div className="h-6 bg-gray-200 rounded w-1/2" />
      <div className="space-y-3">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="h-16 bg-gray-100 rounded" />
        ))}
      </div>
    </div>
  </div>
);

interface AppLoadingShellProps {
  /** Same grid classes as the real layout, so columns line up and don't jump. */
  gridClassName: string;
  /** Status text for screen readers (visual users see the skeletons). */
  message?: string;
}

/**
 * The body of the app while the initial plan is still resolving. Rendered in
 * place of the real 3-column grid (the Header renders above it either way), so
 * the page shows structure immediately instead of a blank full-page spinner.
 * Once currentPlanId resolves, App swaps this for the real columns, which then
 * fill in their own content progressively (each column self-fetches).
 */
const AppLoadingShell: React.FC<AppLoadingShellProps> = ({ gridClassName, message }) => (
  <div className="h-full px-4 sm:px-6 lg:px-8 py-4 lg:py-8 box-border" data-testid="app-loading-shell">
    {message && (
      <div className="sr-only" role="status" aria-live="polite">
        {message}
      </div>
    )}
    <div className={gridClassName}>
      <SkeletonColumn rows={6} />
      <SkeletonColumn rows={5} />
      <SkeletonColumn rows={4} />
    </div>
  </div>
);

export default AppLoadingShell;
